import React, { useState, useEffect, useRef } from 'react';
// GraphQL
import { useMutation } from '@apollo/client';
import { EDIT_USER_IMAGE } from '../graphql/Mutations';
// Components
import { Modal, Image, Button } from 'semantic-ui-react';
import ProfilePlaceholder from '../assets/ProfilePlaceholder.png';
// Interfaces
import { SnackbarActions } from '../Interfaces';
// Helpers
import { handleError } from '../helpers/handleError';
import { getImageSrc } from '../helpers/getImageSrc';
// Firebase
import storage from '../firebase/FirebaseConfig';

type Props = {
    open: boolean
    setOpen: React.Dispatch<React.SetStateAction<boolean>>
    userImage: string
    setUserImage: React.Dispatch<React.SetStateAction<string>>
    userId: string
    snackbarDispatch: React.Dispatch<SnackbarActions>
};

export default function UserImageModal(props: Props) {
    const { open, setOpen, userImage, setUserImage, userId, snackbarDispatch } = props;
    const [image, setImage] = useState({ src: '', alt: '' });
    const [file, setFile] = useState<Blob | Uint8Array | ArrayBuffer>();
    const [uploadingFile, setUploadingFile] = useState(false);

    const [editImage, { loading }] = useMutation<{ edit_user_image: string }>(EDIT_USER_IMAGE, {
        onCompleted: (data) => {
            setUploadingFile(false);
            setUserImage(data.edit_user_image);
            setOpen(false);
        },
        onError: (error): unknown => handleError(error, snackbarDispatch),
    });

    const handleReaderLoaded = (readerEvt: any) => {
        const binaryString = readerEvt.target.result
        setImage({ ...image, src: btoa(binaryString) })
    };

    const handleImg = (e: any) => {
        const file = e.target.files[0]
        if (file) {
            setFile(file);
            setImage({ ...image, alt: file.name })
            const reader = new FileReader();
            reader.onload = handleReaderLoaded;
            reader.readAsBinaryString(file);
        }
    };

    async function handleSubmit() {
        if (file) {
            setUploadingFile(true);
            let imageURL;
            await storage.ref(`U/${userId}/image.jpg`).put(file);
            await storage.ref(`U/${userId}`).child('image.jpg').getDownloadURL().then(url => imageURL = url);
            return editImage({
                variables: {
                    image: imageURL,
                    userId
                }
            })
        };
        return editImage({
            variables: {
                image: '',
                userId
            }
        })
    };

    useEffect(() => {
        setImage({ src: userImage, alt: 'User image' })
    }, []);

    const inputFile: any = useRef(null);
    const onButtonClick = () => inputFile.current.click();

    const imageSrc = image.src && image.src !== '' ? getImageSrc(image.src) : ProfilePlaceholder

    return (
        <Modal open={open} className="user-modal" data-testid="userModal">
            <h2 className="user-modal__title">Edit your image</h2>
            <p className="user-modal__subtitle">It's the image that will be displayed besides your posts and comments</p>
            <div className="user-modal__img-container">
                <Image
                    src={imageSrc}
                    alt={image.alt}
                    className="user-modal__img"
                />
                <input
                    style={{ display: "none" }}
                    accept="image/png, image/jpeg, image/jpg"
                    ref={inputFile}
                    onChange={handleImg}
                    type="file"
                />
                <Button
                    type="button"
                    onClick={() => onButtonClick()}
                    className="user-modal__img-button"
                    disabled={loading || uploadingFile}
                >
                    Change image
                </Button>
            </div>
            <Button.Group fluid>
                <Button
                    color="grey"
                    type="button"
                    onClick={() => setOpen(false)}
                    className="profile-modal__close-button"
                    disabled={loading || uploadingFile}
                >
                    Close
                </Button>
                <Button
                    color="twitter"
                    type="button"
                    className="profile-modal__submit-button"
                    disabled={loading || uploadingFile}
                    onClick={() => handleSubmit()}
                >
                    Edit Image
                </Button>
            </Button.Group>
        </Modal>
    )
};